import http from "./httpService";
import { apiUrl } from "config/envConfig";

const base = `${apiUrl}/payment-mgmt/admin/wallet`;

// Normalize wallet objects coming from various backend shapes
const normalizeWallet = (w) => {
  if (!w || typeof w !== 'object') return w;
  return {
    id: w.id ?? w.Id ?? null,
    driverId: w.driverId ?? w.DriverId ?? null,
    driverName: w.driverName ?? w.DriverName ?? w.driver?.fullName ?? null,
    driverPhoneNumber: w.driverPhoneNumber ?? w.DriverPhoneNumber ?? w.driver?.phone ?? null,
    balance: (w.balance ?? w.Balance) != null ? Number(w.balance ?? w.Balance) : (w.balance ?? null),
    creationDate: w.creationDate ?? w.CreationDate ?? null,
    modificationDate: w.modificationDate ?? w.ModificationDate ?? null,
    raw: w,
  };
};

/**
 * Get all wallets with optional filtering and pagination
 * @param {Object} filter - { pageNumber, pageSize, driverId }
 * @returns {Promise<Object|Array>} Pagination result or array fallback
 */
export const getAllWallets = async (filter = {}) => {
  try {
    const payload = {
      pageNumber: typeof filter.pageNumber !== "undefined" ? filter.pageNumber : 0,
      pageSize: typeof filter.pageSize !== "undefined" ? filter.pageSize : 50,
      // send driverId as provided (GUID string) or null so backend's Guid? binder works
      driverId: filter.driverId ? filter.driverId : null,
    };

    // backend expects POST /GetAllWallets (returns paged result)
    const { data } = await http.post(`${base}/all`, payload);

    // Normalize various backend shapes into a consistent { items, totalCount }
    // Backend may use `data` or `Data` (capitalized) and `page` or `Page`. Support both.
    const raw = data?.data || data?.Data || data || {};

    let items = [];
    if (Array.isArray(raw.items)) items = raw.items;
    else if (Array.isArray(raw.page)) items = raw.page;
    else if (Array.isArray(raw.list)) items = raw.list;
    else if (Array.isArray(raw.Page)) items = raw.Page;
    else if (Array.isArray(raw)) items = raw;

    // normalize items to expected field names used by UI
    const normalizedItems = (items || []).map((it) => normalizeWallet(it));

    const totalCount = raw.totalCount || raw.totalRecords || raw.totalDisplayRecords || raw.TotalRecords || raw.TotalDisplayRecords || normalizedItems.length || 0;

    return { items: normalizedItems, totalCount };
  } catch (error) {
    console.error("Error fetching wallets:", error);
    return { items: [], totalCount: 0 };
  }
};

/**
 * Get wallet by ID
 * @param {string} id - Wallet ID
 * @returns {Promise<Object>} Wallet details
 */
export const getWalletById = async (id) => {
  const { data } = await http.get(`${base}/${id}`);
  // Backend may return payload under `data` or `Data` - normalize both
  const raw = data?.data || data?.Data || data || null;
  return normalizeWallet(raw) || raw;
};

/**
 * Get wallet by driver ID
 * @param {string} driverId - Driver ID
 * @returns {Promise<Object>} Wallet details
 */
export const getWalletByDriverId = async (driverId) => {
  const { data } = await http.get(`${base}/driver/${driverId}`);
  const raw = data?.data || data?.Data || data || null;
  return normalizeWallet(raw) || raw;
};

export default {
  getAllWallets,
  getWalletById,
  getWalletByDriverId,
};
